import { Effect, combine, createEvent, createStore, sample } from 'effector'
import { Request, Response } from './request'

type ListItem = { id: string }

type PageResponse<Item extends ListItem> = {
  items: Item[]
  total: number
}

type ListEffect<Item extends ListItem> = Effect<
  Request<any>,
  Response<PageResponse<Item>>,
  any
>

type PageRequested = {
  key: string
  page: number
  isNewList: boolean
}

type PageReceived<Item extends ListItem> = {
  page: number
  items: Item[]
  total: number
  isRelevant: boolean
}

// Ключ списка — всё, что определяет выборку, кроме номера страницы.
// url обязан входить в ключ: кошелёк передаётся в пути, и без него ответ
// по одному кошельку допишется в список другого.
const getListKey = (params: Request<any>) =>
  JSON.stringify({
    url: params.url ?? '',
    query: Object.fromEntries(
      Object.entries(params.query ?? {})
        .filter(([key]) => key !== 'page')
        .sort(([left], [right]) => left.localeCompare(right)),
    ),
  })

const getPage = (params: Request<any>) => Number(params.query?.page) || 1

/**
 * Накопительный список для бесконечного скролла.
 *
 * Курсор пагинации ($loadedPage) живёт здесь, а не в стейте компонента:
 * счётчик страниц в React разъезжается с реально загруженным при любой
 * потерянной странице (ошибка запроса, отброшенный протухший ответ,
 * дедупликация по id), и подгрузка встаёт навсегда.
 */
export const createInfiniteList = <Item extends ListItem>(
  effect: ListEffect<Item>,
) => {
  const pageRequested = createEvent<PageRequested>()
  const pageReceived = createEvent<PageReceived<Item>>()

  const $listKey = createStore('')
  const $loadedPage = createStore(0)

  sample({
    clock: effect,
    source: $listKey,
    fn: (currentKey, params) => {
      const key = getListKey(params)

      return { key, page: getPage(params), isNewList: key !== currentKey }
    },
    target: pageRequested,
  })

  $listKey.on(pageRequested, (_, { key }) => key)

  sample({
    clock: effect.done,
    source: { key: $listKey, loadedPage: $loadedPage },
    fn: ({ key, loadedPage }, { params, result }) => {
      const page = getPage(params)

      return {
        page,
        items: result.body?.items ?? [],
        total: result.body?.total ?? 0,
        // Ответ принимаем, только если он от текущей выборки и продолжает
        // её без разрыва: иначе протухшая страница (например, page=3,
        // прилетевшая после перезагрузки списка) допишется к свежим данным.
        isRelevant: getListKey(params) === key && page <= loadedPage + 1,
      }
    },
    target: pageReceived,
  })

  const $items = createStore<Item[]>([]).on(
    pageReceived,
    (state, { page, items, isRelevant }) => {
      if (!isRelevant) {
        return state
      }

      if (page <= 1) {
        return items
      }

      const existingIds = new Set(state.map((item) => item.id))

      return [...state, ...items.filter((item) => !existingIds.has(item.id))]
    },
  )

  const $total = createStore(0).on(
    pageReceived,
    (state, { isRelevant, total }) => (isRelevant ? total : state),
  )

  $loadedPage
    .on(pageRequested, (state, { isNewList, page }) =>
      isNewList || page <= 1 ? 0 : state,
    )
    .on(pageReceived, (state, { page, isRelevant }) =>
      isRelevant ? Math.max(state, page) : state,
    )

  // Пустой ответ — единственный надёжный признак, что страницы кончились:
  // длина списка может не дойти до total из-за дедупликации по id.
  const $isExhausted = createStore(false)
    .on(pageRequested, (state, { isNewList, page }) =>
      isNewList || page <= 1 ? false : state,
    )
    .on(pageReceived, (state, { items, isRelevant }) =>
      isRelevant ? items.length === 0 : state,
    )

  const $hasMore = combine(
    $items,
    $total,
    $loadedPage,
    $isExhausted,
    (items, total, loadedPage, isExhausted) =>
      loadedPage > 0 && !isExhausted && items.length < total,
  )

  return { $items, $total, $loadedPage, $hasMore }
}
